import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, RefreshCw, RotateCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getDemandaHoraria,
  getDemandaTopHoras,
  type DemandaSistema,
} from "@/lib/api";

const STORAGE_KEY = "portal-edocta:demanda-horaria-config";
const REFETCH_MS = 30 * 60 * 1000; // 30 minutos

const SISTEMAS: DemandaSistema[] = ["SIN", "BCA", "BCS"];
const PAGE_SIZE = 24; // 24 horas por página = 1 día completo

type SortKey = "key" | "hora" | "demanda_mw" | "generacion_mw" | "pronostico_mw";

type Rango = "hoy" | "7d";
const RANGOS: { key: Rango; label: string }[] = [
  { key: "hoy", label: "Hoy" },
  { key: "7d", label: "Últimos 7 días" },
];

const SERIES = [
  { key: "demanda_mw", label: "Demanda", color: "#1d4ed8" },
  { key: "generacion_mw", label: "Generación", color: "#16a34a" },
  { key: "pronostico_mw", label: "Pronóstico", color: "#d97706" },
] as const;

// Desglose por área (solo el SIN publica áreas): un color fijo por región.
const REGION_COLORS: Record<string, string> = {
  Central: "#1d4ed8",
  Noreste: "#dc2626",
  Noroeste: "#ea580c",
  Norte: "#ca8a04",
  Occidental: "#16a34a",
  Oriental: "#9333ea",
  Peninsular: "#0891b2",
};
const regionColor = (r: string) => REGION_COLORS[r] ?? "#64748b";

// Líneas de referencia del año en curso (mismas que en /demanda): #1 (pico
// anual) y #REF_TOP_N del top de horas. Color distinto a las series.
const REF_TOP_N = 100;
const REF_COLOR = "#be123c"; // rose-700

const INT_FMT = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function fmtMw(v: string | number | null | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  return INT_FMT.format(n);
}

function fmtRelative(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const m = Math.round(diffMs / 60_000);
  if (m < 1) return "hace <1 min";
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  const days = Math.round(h / 24);
  return `hace ${days} d`;
}

function tickLabel(key: string): string {
  // key = "YYYY-MM-DD HH"
  const [d, h] = key.split(" ");
  const [, mm, dd] = d.split("-");
  return `${dd}/${mm} ${h}:00`;
}

export function DemandaHorariaPage() {
  const stored = useMemo<{ sistema?: DemandaSistema; rango?: Rango; desglose?: boolean }>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [sistema, setSistema] = useState<DemandaSistema>(stored.sistema ?? "SIN");
  const [rango, setRango] = useState<Rango>(stored.rango ?? "hoy");
  const [desglose, setDesglose] = useState<boolean>(stored.desglose ?? false);
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState<SortKey>("key");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Solo el SIN publica áreas; en BCA/BCS el toggle se ignora (y se oculta).
  const desgloseActivo = desglose && sistema === "SIN";

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ sistema, rango, desglose }));
    } catch {
      // ignore
    }
  }, [sistema, rango, desglose]);

  // Reset paginación cuando cambian filtros principales o el orden.
  useEffect(() => {
    setPage(0);
  }, [sistema, rango, sortKey, sortDir, desgloseActivo]);

  const handleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir("desc");
    }
  };

  // Resuelve `desde` en ISO local (YYYY-MM-DD) según el rango. Sin `hasta`
  // para permitir incluir el pronóstico futuro publicado en la tabla.
  const desde = useMemo(() => {
    const d = new Date();
    if (rango === "7d") d.setDate(d.getDate() - 7);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, [rango]);

  const q = useQuery({
    queryKey: ["demanda-horaria", sistema, desde, desgloseActivo],
    queryFn: () => getDemandaHoraria({ sistema, desde, desglose: desgloseActivo }),
    refetchInterval: REFETCH_MS,
    refetchIntervalInBackground: true,
    staleTime: REFETCH_MS / 2,
  });

  // Top del AÑO EN CURSO (anios=1) para las líneas de referencia: #1 (pico
  // anual) y #REF_TOP_N. Mismo endpoint que alimenta /demanda.
  const topAnioQ = useQuery({
    queryKey: ["demanda-horaria:top-anio", sistema, REF_TOP_N],
    queryFn: () => getDemandaTopHoras({ sistema, anios: 1, n: REF_TOP_N }),
    staleTime: REFETCH_MS,
  });

  const refsAnio = useMemo(() => {
    const items = topAnioQ.data?.items ?? [];
    if (items.length === 0) return null;
    const r1 = items.find((r) => r.posicion === 1)?.demanda_mwh;
    if (r1 == null) return null;
    const v1 = Number(r1);
    if (!Number.isFinite(v1)) return null;
    const rN = items.find((r) => r.posicion === REF_TOP_N)?.demanda_mwh;
    const vN = rN != null ? Number(rN) : NaN;
    return {
      anio: items[0].anio,
      rank1: v1,
      rankN: Number.isFinite(vN) ? vN : null,
    };
  }, [topAnioQ.data]);

  const data = useMemo(() => {
    if (!q.data) return [];
    return q.data.items.map((r) => ({
      key: `${r.fecha} ${pad(r.hora)}`,
      fecha: r.fecha,
      hora: r.hora,
      region: r.region,
      demanda_mw: r.demanda_mw,
      generacion_mw: r.generacion_mw,
      pronostico_mw: r.pronostico_mw,
    }));
  }, [q.data]);

  const regiones = q.data?.regiones ?? [];

  // Orden del apilado: cada clic en "Rotar orden" recorre las series una
  // posición (la siguiente área pasa a la base del stack).
  const [ordenOffset, setOrdenOffset] = useState(0);
  const regionesOrdenadas = useMemo(() => {
    if (regiones.length === 0) return regiones;
    const off = ((ordenOffset % regiones.length) + regiones.length) % regiones.length;
    return [...regiones.slice(off), ...regiones.slice(0, off)];
  }, [regiones, ordenOffset]);

  // Pivot para la gráfica desglosada: una fila por hora con la DEMANDA de cada
  // área como columna (la generación/pronóstico por área quedan en la tabla).
  const dataDesglose = useMemo(() => {
    if (!desgloseActivo) return [];
    const byKey = new Map<string, Record<string, number | string>>();
    for (const r of data) {
      let slot = byKey.get(r.key);
      if (!slot) {
        slot = { key: r.key };
        byKey.set(r.key, slot);
      }
      if (r.demanda_mw != null) slot[r.region] = r.demanda_mw;
    }
    return [...byKey.values()].sort((a, b) =>
      String(a.key).localeCompare(String(b.key)),
    );
  }, [data, desgloseActivo]);

  // Rango del eje Y dinámico según los datos mostrados (sin piso fijo): encuadra
  // los valores visibles con un pequeño margen. Las líneas de referencia del año
  // extienden el dominio si caen fuera (ifOverflow="extendDomain").
  const yDomain = useMemo<[number, number]>(() => {
    const vals: number[] = [];
    for (const row of data) {
      for (const s of SERIES) {
        const v = row[s.key];
        if (v == null) continue;
        const n = Number(v);
        if (Number.isFinite(n)) vals.push(n);
      }
    }
    if (vals.length === 0) return [0, 1];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const range = hi - lo || Math.max(1, hi * 0.05);
    const pad = range * 0.08;
    return [
      Math.max(0, Math.floor((lo - pad) / 100) * 100),
      Math.ceil((hi + pad) / 100) * 100,
    ];
  }, [data]);

  // Marcas del eje Y cada 2,000 MW (más líneas de referencia horizontales).
  // Cubre también el rango que las líneas Máx/Min del año pueden extender.
  const yTicks = useMemo(() => {
    // Paso del eje según el tamaño del sistema: SIN 2,000 · BCA 200 · BCS 50.
    const step = sistema === "SIN" ? 2000 : sistema === "BCA" ? 200 : 50;
    let [lo, hi] = yDomain;
    if (refsAnio) {
      hi = Math.max(hi, refsAnio.rank1);
      if (refsAnio.rankN != null) lo = Math.min(lo, refsAnio.rankN);
    }
    const start = Math.ceil(lo / step) * step;
    const ticks: number[] = [];
    for (let t = start; t <= hi; t += step) ticks.push(t);
    return ticks;
  }, [yDomain, refsAnio, sistema]);

  // Misma data pero ordenada según la columna activa.
  const tableData = useMemo(() => {
    const rows = [...data];
    rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "key") {
        cmp = a.key.localeCompare(b.key);
      } else if (sortKey === "hora") {
        cmp = a.hora - b.hora;
      } else {
        // Columnas numéricas con nullables — null al final.
        const av = a[sortKey];
        const bv = b[sortKey];
        if (av == null && bv == null) cmp = 0;
        else if (av == null) cmp = 1;
        else if (bv == null) cmp = -1;
        else cmp = Number(av) - Number(bv);
      }
      // Tiebreak por timestamp ascendente para que el orden sea estable.
      if (cmp === 0) cmp = a.key.localeCompare(b.key);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const totalPages = Math.ceil(tableData.length / PAGE_SIZE);
  const tablePage = useMemo(
    () => tableData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [tableData, page],
  );

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              Demanda horaria en vivo
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Demanda, generación y pronóstico horario por sistema (
              {rango === "hoy" ? "hoy" : "últimos 7 días"} + pronóstico futuro
              disponible). Refresco automático cada 30 minutos. Fuente:{" "}
              <code className="font-mono">public.demanda_horaria</code>.
              {desgloseActivo ? (
                <> Desglose: demanda por área del SIN apilada — el techo del
                  stack es la demanda total del sistema (la generación y el
                  pronóstico por área quedan en la tabla).</>
              ) : (
                refsAnio && (
                  <>
                    {" "}Líneas rojas punteadas: pico anual (#1) y umbral top-
                    {REF_TOP_N} de la demanda {refsAnio.anio}.
                  </>
                )
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {q.data && (
              <div className="text-[11px] text-slate-500 leading-tight flex items-center gap-1">
                <RefreshCw
                  className={`w-3 h-3 ${q.isFetching ? "animate-spin" : ""}`}
                />
                <div className="flex flex-col">
                  <span>
                    Actualizado:{" "}
                    <b className="text-slate-700">
                      {fmtRelative(q.data.ultima_actualizacion)}
                    </b>
                  </span>
                  <span className="text-slate-400">{data.length} horas</span>
                </div>
              </div>
            )}
            <div className="inline-flex border rounded overflow-hidden">
              {RANGOS.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRango(r.key)}
                  className={`px-3 py-1.5 text-sm border-l first:border-l-0 ${rango === r.key
                    ? "bg-blue-600 text-white"
                    : "bg-white hover:bg-slate-50 text-slate-700"
                    }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="inline-flex border rounded overflow-hidden">
              {SISTEMAS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSistema(s)}
                  className={`px-3 py-1.5 text-sm border-l first:border-l-0 ${sistema === s
                    ? "bg-blue-600 text-white"
                    : "bg-white hover:bg-slate-50 text-slate-700"
                    }`}
                >
                  {s}
                </button>
              ))}
            </div>
            {sistema === "SIN" && (
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-700 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={desglose}
                  onChange={(e) => setDesglose(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Desglose por área
              </label>
            )}
          </div>
        </div>
      </div>

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          Cargando…
        </div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          Error: {(q.error as Error).message}
        </div>
      )}

      {q.data && data.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          Sin datos disponibles para {sistema}.
        </div>
      )}

      {data.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          {desgloseActivo && regionesOrdenadas.length > 1 && (
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => setOrdenOffset((o) => o + 1)}
                className="inline-flex items-center gap-1.5 border rounded px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50"
                title="Rota el orden del apilado: la siguiente área pasa a la base"
              >
                <RotateCw className="w-3.5 h-3.5" />
                Rotar orden
                <span className="text-slate-400">
                  (base: <b className="text-slate-600">{regionesOrdenadas[0]}</b>)
                </span>
              </button>
            </div>
          )}
          <div className="h-[520px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={desgloseActivo ? dataDesglose : data}
                margin={{ top: 10, right: 24, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="key"
                  tick={{ fontSize: 11, fill: "#475569" }}
                  tickFormatter={tickLabel}
                  minTickGap={48}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#475569" }}
                  tickFormatter={(v) => INT_FMT.format(Number(v))}
                  width={72}
                  // Eje Y dinámico: encuadra los datos mostrados (yDomain). Con
                  // desglose (áreas apiladas) arranca en 0 y el techo lo pone
                  // recharts con el total del stack.
                  domain={desgloseActivo ? [0, "auto"] : yDomain}
                  ticks={desgloseActivo ? undefined : yTicks}
                  interval={0}
                  allowDataOverflow
                  label={{
                    value: "MW",
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 11, fill: "#64748b" },
                  }}
                />
                <Tooltip
                  labelFormatter={(label) => tickLabel(String(label))}
                  formatter={(value: number | string, name: string) => {
                    if (desgloseActivo) return [`${fmtMw(value)} MW`, name];
                    const meta = SERIES.find((s) => s.key === name);
                    return [`${fmtMw(value)} MW`, meta?.label ?? name];
                  }}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  formatter={(value) =>
                    desgloseActivo
                      ? value
                      : SERIES.find((s) => s.key === value)?.label ?? value
                  }
                />
                {desgloseActivo
                  ? // Áreas APILADAS: cada banda es un área del SIN y el techo
                    // del stack es la demanda total del sistema.
                    regionesOrdenadas.map((r) => (
                    <Area
                      key={r}
                      type="monotone"
                      dataKey={r}
                      stackId="areas"
                      stroke={regionColor(r)}
                      strokeWidth={1}
                      fill={regionColor(r)}
                      fillOpacity={0.55}
                      activeDot={{ r: 3 }}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))
                  : SERIES.map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      dataKey={s.key}
                      stroke={s.color}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                      connectNulls
                    />
                  ))}
                {!desgloseActivo && refsAnio && (
                  <ReferenceLine
                    y={refsAnio.rank1}
                    stroke={REF_COLOR}
                    strokeWidth={1.5}
                    strokeDasharray="7 4"
                    ifOverflow="extendDomain"
                    label={{
                      value: `Máx ${refsAnio.anio}: ${INT_FMT.format(Math.round(refsAnio.rank1))} MW`,
                      position: "insideBottomRight",
                      fontSize: 11,
                      fill: REF_COLOR,
                    }}
                  />
                )}
                {!desgloseActivo && refsAnio?.rankN != null && (
                  <ReferenceLine
                    y={refsAnio.rankN}
                    stroke={REF_COLOR}
                    strokeWidth={1.25}
                    strokeDasharray="2 3"
                    ifOverflow="extendDomain"
                    label={{
                      value: `Min ${refsAnio.anio}: ${INT_FMT.format(Math.round(refsAnio.rankN))} MW`,
                      position: "insideTopRight",
                      fontSize: 11,
                      fill: REF_COLOR,
                    }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tableData.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b">
            <h3 className="text-sm font-semibold text-slate-800">
              Datos horarios ({tableData.length.toLocaleString("es-MX")} horas)
              <span className="ml-2 text-[11px] font-normal text-slate-500">
                — click en un encabezado para ordenar
              </span>
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <SortableTh col="key" align="left" current={sortKey} dir={sortDir} onSort={handleSort}>
                  Fecha
                </SortableTh>
                <SortableTh col="hora" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>
                  Hora
                </SortableTh>
                {desgloseActivo && (
                  <th className="px-3 py-2 font-medium text-left">Área</th>
                )}
                <SortableTh col="demanda_mw" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: SERIES[0].color }}
                  />
                  Demanda (MW)
                </SortableTh>
                <SortableTh col="generacion_mw" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: SERIES[1].color }}
                  />
                  Generación (MW)
                </SortableTh>
                <SortableTh col="pronostico_mw" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: SERIES[2].color }}
                  />
                  Pronóstico (MW)
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {tablePage.map((r) => (
                <tr key={`${r.key}-${r.region}`} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-1.5">{r.fecha}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {pad(r.hora)}:00
                  </td>
                  {desgloseActivo && (
                    <td className="px-3 py-1.5">
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-1.5"
                        style={{ backgroundColor: regionColor(r.region) }}
                      />
                      {r.region}
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                    {fmtMw(r.demanda_mw)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {fmtMw(r.generacion_mw)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                    {fmtMw(r.pronostico_mw)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t flex items-center justify-between text-sm">
              <div className="text-slate-500">
                Página {page + 1} de {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="border rounded px-3 py-1 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  className="border rounded px-3 py-1 disabled:opacity-50"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page + 1 >= totalPages}
                >
                  Siguiente →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SortableTh({
  col,
  align,
  current,
  dir,
  onSort,
  children,
}: {
  col: SortKey;
  align: "left" | "right";
  current: SortKey;
  dir: "asc" | "desc";
  onSort: (k: SortKey) => void;
  children: React.ReactNode;
}) {
  const isActive = current === col;
  const Icon = !isActive ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`px-3 py-2 font-medium select-none cursor-pointer hover:bg-slate-200 ${align === "right" ? "text-right" : ""
        }`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <Icon
          className={`w-3 h-3 ${isActive ? "" : "opacity-40"}`}
          aria-hidden="true"
        />
      </span>
    </th>
  );
}
